/**
 * Panel de actividades del profesor
 * Muestra lista de actividades creadas con opciones para crear, editar y copiar URLs
 */

import React, { useState, useEffect } from 'react';
import { teacherActivityService } from '../../services/teacherActivityService';
import { TeacherActivity } from '../../types';
import configService from '../../services/configService';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import './TeacherActivityPanel.css';

interface TeacherActivityPanelProps {
  onClose?: () => void;
  onNewActivity?: () => void;
  onEditActivity?: (activity: TeacherActivity) => void;
}

const TeacherActivityPanel: React.FC<TeacherActivityPanelProps> = ({
  onClose,
  onNewActivity,
  onEditActivity,
}) => {
  const [activities, setActivities] = useState<TeacherActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const orientation = useScreenOrientation();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const config = configService.getConfig();
      const email = config.email;
      if (!email) {
        console.error('[TeacherActivityPanel] No hay email en config');
        return;
      }

      const teacherActivities = await teacherActivityService.getTeacherActivities(email);
      setActivities(teacherActivities);
    } catch (error) {
      console.error('[TeacherActivityPanel] Error al cargar actividades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async (activity: TeacherActivity) => {
    if (!activity.tache_id) {
      alert('Esta actividad aún no está desplegada. Despliégala primero para obtener una URL.');
      return;
    }

    const url = teacherActivityService.generateActivityUrl(activity.tache_id);
    try {
      await navigator.clipboard.writeText(url);
      alert('URL copiada al portapapeles: ' + url);
    } catch (error) {
      console.error('[TeacherActivityPanel] Error al copiar URL:', error);
      // Fallback: mostrar URL en un prompt
      prompt('Copia esta URL:', url);
    }
  };

  const handleDeleteActivity = async (activity: TeacherActivity) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta actividad?' + (activity.is_deployed ? ' La actividad está desplegada y dejará de estar disponible por URL.' : ''))) {
      return;
    }

    const config = configService.getConfig();
    const email = config.email;
    if (!email) return;

    const deleted = await teacherActivityService.deleteActivity(activity.id!, email);
    if (deleted) {
      loadActivities(); // Recargar lista
    } else {
      alert('Error al eliminar la actividad.');
    }
  };

  const getStatusBadge = (isDeployed: boolean) => {
    return (
      <span className={`status-badge ${isDeployed ? 'deployed' : 'draft'}`}>
        {isDeployed ? 'Desplegada' : 'Borrador'}
      </span>
    );
  };

  const truncateText = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={`teacher-activity-panel ${orientation}`}>
      <div className="teacher-activity-panel-content">
        {orientation === 'portrait' && onClose && (
          <div className="teacher-activity-panel-header-portrait">
            <div className="teacher-activity-panel-header-portrait-top">
              <h2>Mis Actividades</h2>
              <button
                className="teacher-activity-panel-close-button"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <img 
                  src="/assets/ui/botones/Boton_Cerrar.png" 
                  alt="Cerrar"
                  className="teacher-activity-panel-close-icon"
                />
              </button>
            </div>
          </div>
        )}

        {orientation === 'landscape' && (
          <div className="teacher-activity-panel-header">
            <div className="teacher-activity-panel-header-top">
              <h2>Mis Actividades</h2>
              {onClose && (
                <button
                  className="teacher-activity-panel-close-button-landscape"
                  onClick={onClose}
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  <img 
                    src="/assets/ui/botones/Boton_Cerrar.png" 
                    alt="Cerrar"
                    className="teacher-activity-panel-close-icon"
                  />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="teacher-activity-panel-body">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-message">Cargando actividades...</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="empty-state">
              <p>No has creado ninguna actividad aún.</p>
              <button className="new-activity-button" onClick={onNewActivity}>
                + Crear Primera Actividad
              </button>
            </div>
          ) : (
            <>
              <button className="new-activity-button" onClick={onNewActivity}>
                + Nueva Actividad
              </button>
              <div className="activities-list">
                {activities.map((activity) => (
                  <div key={activity.id} className="activity-card">
                    <div className="activity-card-header">
                      {getStatusBadge(activity.is_deployed)}
                      {activity.classroom_id && (
                        <span className="classroom-badge">Aula: {activity.classroom_id}</span>
                      )}
                      {activity.level && (
                        <span className="level-badge">Nivel: {activity.level}</span>
                      )}
                    </div>
                    <div className="activity-card-body">
                      <p className="activity-description">
                        {truncateText(activity.task_description)}
                      </p>
                      <p className="activity-objectives-count">
                        {activity.objectives.length} objetivo{activity.objectives.length !== 1 ? 's' : ''}
                      </p>
                      {activity.created_at && (
                        <p className="activity-date">
                          Creada: {new Date(activity.created_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="activity-card-actions">
                      <button
                        className="action-button edit"
                        onClick={() => onEditActivity?.(activity)}
                      >
                        {activity.is_deployed ? 'Ver' : 'Editar'}
                      </button>
                      {activity.is_deployed && activity.tache_id && (
                        <button
                          className="action-button copy-url"
                          onClick={() => handleCopyUrl(activity)}
                        >
                          Copiar URL
                        </button>
                      )}
                      <button
                        className="action-button delete"
                        onClick={() => handleDeleteActivity(activity)}
                        title="Eliminar actividad"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherActivityPanel;
